import { FormData } from "../Components/Form/Form";
import instance from "../middleware/HTTPInterceptors";
import ErrorResponse from "../utilities/ErrorResponse";
import * as T from "../utilities/frontendTypes";
import { apiGet, apiGetBlob, apiPost, apiPut } from "./apiClient";
import { getAuthHeader } from "../utilities/helperFunctions";

/*

  Program, Membership & Application Services

*/
export async function createFounderApplication(programId: T.ProgramId): Promise<T.FounderMembershipApplicationResponse> {
  return apiPost<T.FounderMembershipApplicationResponse>("/api/founders/application", { programId });
}

export async function getLatestMembershipApplication(): Promise<T.HTTPResponse> {
  return apiGet<T.HTTPResponse>("/api/founders/applications/latest-membership-application");
}

export async function getAllPrograms(): Promise<T.FounderProgramsResponse> {
  return apiGet<T.FounderProgramsResponse>("/api/founders/programs");
}

export async function getProgramDetails(programId: T.ProgramId): Promise<T.ProgramDetailsResponse> {
  return apiGet<T.ProgramDetailsResponse>(`/api/founders/programs/${programId}`);
}

export async function createFounderProgramApplication(programId: T.ProgramId, additionalData: FormData | undefined): Promise<T.FounderMembershipApplicationResponse> {
  return apiPost<T.FounderMembershipApplicationResponse>("/api/founders/program-application", { programId, additionalData });
}

export async function founderProgramEnroll(programId: T.ProgramId): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/founders/program-enrollment", { programId });
}

export async function getFounderApplicationHeaders(): Promise<T.FounderApplicationHeadersResponse> {
  return apiGet<T.FounderApplicationHeadersResponse>("/api/founders/founder-applications");
}

export async function getFounderProgramApplicationDetails(id: string): Promise<T.ApplicationDetailsResponse> {
  return apiGet<T.ApplicationDetailsResponse>(`/api/founders/program-application/${id}`);
}

export async function getEnrolledPrograms(): Promise<T.EnrolledProgramsResponse> {
  return apiGet<T.EnrolledProgramsResponse>("/api/founders/enrolled-programs");
}

/*

  Service Provider Firms Services

*/
export async function getSPFirms(): Promise<T.HTTPResponse> {
  return apiGet<T.HTTPResponse>("/api/founders/sp-firms");
}

export async function getSPFirmDetailsById(firmId: T.FirmId): Promise<T.HTTPResponse> {
  return apiGet<T.HTTPResponse>(`/api/founders/sp-firms/${firmId}`);
}

export async function sendHelpEmail(emailDetails: {message: string, helpCategory: string}): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>("/api/founders/help", emailDetails);
}

export async function sendContactEmail(emailDetails: {subject: string, message: string, email: string}): Promise<T.HTTPResponse> {
  try {
    const res = await instance.put(`/api/public/contact-us`, emailDetails);

    if (res.status === 200) {
      return res.data;
    }

    throw new ErrorResponse("Request unsuccessful", 400);

  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string }; status?: number } };
    throw new ErrorResponse(axiosErr?.response?.data?.message || "Failed to send message", axiosErr?.response?.status || 500);
  }
}

/*

  Portal Control Services

*/
export async function getNewMemberAcceptanceStatus(): Promise<T.HTTPResponse> {
  return apiGet<T.HTTPResponse>("/api/founders/new-member-acceptance-status");
}

export async function getIntakeStatus(): Promise<T.HTTPResponse> {
  return apiGet<T.HTTPResponse>("/api/founders/intake-status");
}

export async function pdfUpload(form: globalThis.FormData): Promise<T.HTTPRequestSuccessFlag> {
  try {
    const config = {
      headers: await getAuthHeader(),
      'Content-Type': 'multipart/form-data'
    }
    const res = await instance.post('/api/founders/upload-file', form, config)

    return res.data.success;

  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string }; status?: number } };
    throw new ErrorResponse(axiosErr.response?.data?.message || "Upload failed", axiosErr.response?.status || 500);
  }
};

export async function getDownloadUrl(document: string): Promise<{success: boolean, url: string }> {
  return apiGet<{success: boolean, url: string}>(`/api/founders/get-pdf/${document}`);
}

export async function getPdfBytes(docKey: string): Promise<ArrayBuffer> {
  return apiGetBlob(`/api/founders/get-pdf-bytes/${docKey}`);
}

export async function getfounderDashboardDetails(): Promise<{success: boolean, dashboardDetails: T.FounderDashboardDetails }> {
  return apiGet<{success: boolean, dashboardDetails: T.FounderDashboardDetails}>("/api/founders/get-dashboard-details");
}


export async function addIdAsync(): Promise<T.HTTPResponse> {
  try {
    const config = {
      headers: await getAuthHeader(),
    }

    const res = await instance.put(`/api/founders/learning-id`, config);

    if (res.status === 200) {
      return res.data
    }

    throw new ErrorResponse("Request unsuccessful", 400);

  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string }; status?: number } };
    throw new ErrorResponse(axiosErr.response?.data?.message || "Request failed", axiosErr.response?.status || 500);
  }
}


export async function founderApproveSow(sowId: string): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>("/api/founders/approve-sow", { sowId });
}

export async function editedApplicationChange(): Promise<T.FounderMembershipApplicationResponse> {
  return apiPost<T.FounderMembershipApplicationResponse>("/api/founders/application-edit-request-update");
}

export async function getInProgApp(): Promise<{success: boolean, inProg: boolean, userRegion: string, }> {
  return apiGet<{success: boolean, inProg: boolean, userRegion: string}>("/api/founders/application-in-prog-check");
}

export async function sendSpfConfirm(spf: string): Promise<T.HTTPResponse> {
  return apiPut<T.HTTPResponse>("/api/founders/send-spf-confirm", { spf });
}

export async function reapplyFromCompletedProject(): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/founders/reapply");
}

export async function sendSpfRequest(newSpfDetails: string): Promise<T.HTTPResponse> {
  return apiPost<T.HTTPResponse>("/api/founders/send-spf-request", { newSpfDetails });
}

import type { InPortalSignerInfo } from "@eip/shared/types/sow.types";

export async function getFounderSigningInfo(sowId: string): Promise<InPortalSignerInfo> {
  return apiGet<InPortalSignerInfo>(`/api/founders/sow/${sowId}/signing-info`);
}

export async function submitFounderSignature(sowId: string, signatureBase64: string, consent: boolean, printedName: string): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>(`/api/founders/sow/${sowId}/sign`, { signatureBase64, consent, printedName });
}
